import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { userProfileService } from "../../api/services/userProfileService";
import {
  getUserData,
  isUserLoggedIn,
  setUserLoggedIn,
  storedUserEmailMatches,
} from "../../utils/authUtils";
import { getQueryParamRaw } from "../../utils/queryStringUtils";
import Loader from "../Loader/Loader";
import "./ConfirmEmail.css";

const ConfirmEmail = () => {
  const { search } = useLocation();
  const email = useMemo(
    () => getQueryParamRaw(search, "email").trim(),
    [search],
  );
  const token = useMemo(
    () => getQueryParamRaw(search, "token").trim(),
    [search],
  );
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("pending");

  const hasRequiredParams = useMemo(() => !!email && !!token, [email, token]);

  useEffect(() => {
    if (email) localStorage.setItem("confirm_email", email);
    if (token) localStorage.setItem("confirm_token", token);

    const run = async () => {
      if (!hasRequiredParams) {
        setStatus("error");
        setMessage(
          "Invalid confirmation link. Please request a new confirmation email.",
        );
        setLoading(false);
        return;
      }

      setLoading(true);
      const minDelay = new Promise((resolve) => setTimeout(resolve, 1500));

      try {
        const [res] = await Promise.all([
          userProfileService.confirmEmail(email, token),
          minDelay,
        ]);
        const ok =
          Number(res?.success) === 1 ||
          res?.success === true ||
          res?.success === "1";
        setStatus(ok ? "success" : "error");
        setMessage(
          res?.msg ??
            res?.message ??
            (ok
              ? "Your email has been confirmed successfully."
              : "Unable to confirm your email."),
        );
        if (ok) {
          try {
            const ud = getUserData();
            if (
              isUserLoggedIn() &&
              ud &&
              storedUserEmailMatches(ud, email)
            ) {
              setUserLoggedIn({
                ...ud,
                IsUserEmailAddressVerified: true,
              });
            }
          } catch (_) {
            /* ignore */
          }
        }
      } catch (err) {
        await minDelay;
        setStatus("error");
        setMessage(
          err?.response?.data?.msg ??
            err?.response?.data?.message ??
            err?.message ??
            "Unable to confirm your email. Please try again.",
        );
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [email, token, hasRequiredParams]);

  return (
    <div className="confirm-email-page">
      {loading && <Loader />}
      <div className="confirm-email-card">
        <div
          className={`confirm-email-card-top confirm-email-card-top--${status}`}
        />
        <div className="confirm-email-card-body">
          <h1 className="confirm-email-title">Email Confirmation</h1>
          <p
            className={`confirm-email-message confirm-email-message--${status}`}
            role="status"
            aria-live="polite"
          >
            {message}
          </p>
          <div className="confirm-email-actions">
            <Link to="/login" className="confirm-email-btn">
              Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmEmail;
